/**
 * COMMERCE-LAUNCH-168D2 — owner-only Android Telegram IME forensic harness.
 * Allowlist: 475734587 + ?ime_debug=1. Never logs typed text (lengths/counts only).
 * Ordinary customers never see this route.
 */
import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { IsolatedSearchBar } from "./IsolatedSearchBar.js";
import { IsolatedFormField } from "./IsolatedFormField.js";
import { filterPersonNameInput } from "./checkout-validation-v2.js";
import { telegramUserId } from "./customer-profile-v2.js";
import type { StoreV2Lang } from "./store-v2-locale.js";

const OWNER_ALLOWLIST = new Set(["475734587"]);

export function isOwnerImeDebugAllowed(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get("ime_debug") !== "1") return false;
  } catch {
    return false;
  }
  const uid = telegramUserId();
  return Boolean(uid && OWNER_ALLOWLIST.has(uid));
}

type Counters = {
  pointerdown: number;
  click: number;
  focus: number;
  focusin: number;
  beforeinput: number;
  input: number;
  change: number;
  compositionstart: number;
  compositionupdate: number;
  compositionend: number;
  keydown: number;
  keyup: number;
  blur: number;
};

const EMPTY_COUNTERS = (): Counters => ({
  pointerdown: 0,
  click: 0,
  focus: 0,
  focusin: 0,
  beforeinput: 0,
  input: 0,
  change: 0,
  compositionstart: 0,
  compositionupdate: 0,
  compositionend: 0,
  keydown: 0,
  keyup: 0,
  blur: 0,
});

type PanelState = {
  focused: boolean;
  valueLength: number;
  activeElement: string;
  counters: Counters;
  mountId: string;
  mountCount: number;
  unmountCount: number;
  renderCount: number;
  nodeIdBeforeKeyboard: string;
  nodeIdAfterKeyboard: string;
  nodeIdAfterFirstKey: string;
  nodeIdAfterViewport: string;
  lastEvent: string;
  lastEventAt: string;
  remountSuspected: boolean;
  focusStealSuspected: boolean;
};

function describeActive(): string {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return "null";
  const id = el.id ? `#${el.id}` : "";
  const test = el.getAttribute?.("data-ime-test") ?? el.getAttribute?.("data-testid") ?? "";
  return `${el.tagName.toLowerCase()}${id}${test ? `[${test}]` : ""}`;
}

function nodeIdentity(el: Element | null): string {
  if (!el) return "none";
  // Object identity proxy: tag + dataset attribute we stamp + dataset token stored on element.
  const stamped = (el as HTMLElement).dataset?.imeNodeId ?? "";
  return stamped || "unstamped";
}

function stampNode(el: HTMLElement, prefix: string): string {
  if (!el.dataset.imeNodeId) {
    el.dataset.imeNodeId = `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
  }
  return el.dataset.imeNodeId;
}

function ViewportPanel() {
  const [vp, setVp] = useState(() => readViewport());
  useEffect(() => {
    const update = () => setVp(readViewport());
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    const tg = (window as unknown as { Telegram?: { WebApp?: { onEvent?: (e: string, cb: () => void) => void; offEvent?: (e: string, cb: () => void) => void } } })
      .Telegram?.WebApp;
    try {
      tg?.onEvent?.("viewportChanged", update);
      tg?.onEvent?.("safeAreaChanged", update);
      tg?.onEvent?.("contentSafeAreaChanged", update);
    } catch {
      /* ignore */
    }
    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
      try {
        tg?.offEvent?.("viewportChanged", update);
        tg?.offEvent?.("safeAreaChanged", update);
        tg?.offEvent?.("contentSafeAreaChanged", update);
      } catch {
        /* ignore */
      }
    };
  }, []);
  return (
    <pre className="sv2-ime-diag-pre" data-testid="sv2-ime-viewport">
      {`innerHeight=${vp.innerHeight}
visualViewport.height=${vp.vvHeight}
visualViewport.offsetTop=${vp.vvOffsetTop}
tgViewportHeight=${vp.tgHeight}
tgViewportStableHeight=${vp.tgStable}
keyboardLikely=${vp.keyboardLikely}`}
    </pre>
  );
}

function readViewport() {
  const vv = window.visualViewport;
  const tg = (window as unknown as { Telegram?: { WebApp?: { viewportHeight?: number; viewportStableHeight?: number } } })
    .Telegram?.WebApp;
  const innerHeight = window.innerHeight;
  const vvHeight = vv?.height ?? -1;
  const shrink = vv ? innerHeight - vv.height : 0;
  return {
    innerHeight,
    vvHeight,
    vvOffsetTop: vv?.offsetTop ?? -1,
    tgHeight: tg?.viewportHeight ?? -1,
    tgStable: tg?.viewportStableHeight ?? -1,
    keyboardLikely: shrink > 120,
  };
}

function TelemetryBox({ title, state }: { title: string; state: PanelState }) {
  const c = state.counters;
  return (
    <pre className="sv2-ime-diag-pre" data-testid={`sv2-ime-telemetry-${title}`}>
      {`${title}
focused=${state.focused}
valueLength=${state.valueLength}
activeElement=${state.activeElement}
pointerdown=${c.pointerdown} click=${c.click}
focus=${c.focus} focusin=${c.focusin}
beforeinput=${c.beforeinput} input=${c.input} change=${c.change}
compositionstart=${c.compositionstart} compositionupdate=${c.compositionupdate} compositionend=${c.compositionend}
keydown=${c.keydown} keyup=${c.keyup} blur=${c.blur}
mountId=${state.mountId}
mountCount=${state.mountCount} unmountCount=${state.unmountCount} renderCount=${state.renderCount}
nodeId before keyboard=${state.nodeIdBeforeKeyboard}
nodeId after keyboard=${state.nodeIdAfterKeyboard}
nodeId after first key=${state.nodeIdAfterFirstKey}
nodeId after viewport=${state.nodeIdAfterViewport}
remountSuspected=${state.remountSuspected}
focusStealSuspected=${state.focusStealSuspected}
lastEvent=${state.lastEvent} @ ${state.lastEventAt}`}
    </pre>
  );
}

function useImePanel(testId: string) {
  const mountIdRef = useRef(`${testId}-${Date.now().toString(36)}`);
  const mountCountRef = useRef(0);
  const unmountCountRef = useRef(0);
  const renderCountRef = useRef(0);
  const firstKeySeen = useRef(false);
  const [state, setState] = useState<PanelState>(() => ({
    focused: false,
    valueLength: 0,
    activeElement: "n/a",
    counters: EMPTY_COUNTERS(),
    mountId: mountIdRef.current,
    mountCount: 0,
    unmountCount: 0,
    renderCount: 0,
    nodeIdBeforeKeyboard: "—",
    nodeIdAfterKeyboard: "—",
    nodeIdAfterFirstKey: "—",
    nodeIdAfterViewport: "—",
    lastEvent: "none",
    lastEventAt: "—",
    remountSuspected: false,
    focusStealSuspected: false,
  }));

  renderCountRef.current += 1;

  useEffect(() => {
    mountCountRef.current += 1;
    setState((s) => ({ ...s, mountCount: mountCountRef.current, renderCount: renderCountRef.current }));
    return () => {
      unmountCountRef.current += 1;
    };
  }, []);

  const bump = useCallback(
    (eventName: keyof Counters | "meta", el: HTMLElement | null, extra?: Partial<PanelState>) => {
      const id = el ? stampNode(el, testId) : "none";
      const vv = window.visualViewport;
      const keyboardLikely = vv ? window.innerHeight - vv.height > 120 : false;
      setState((s) => {
        const counters = { ...s.counters };
        if (eventName !== "meta") counters[eventName] += 1;
        const next: PanelState = {
          ...s,
          ...extra,
          counters,
          focused: document.activeElement === el,
          valueLength: el && "value" in el ? String((el as HTMLInputElement).value ?? "").length : s.valueLength,
          activeElement: describeActive(),
          renderCount: renderCountRef.current,
          mountCount: mountCountRef.current,
          unmountCount: unmountCountRef.current,
          lastEvent: String(eventName),
          lastEventAt: new Date().toISOString().slice(11, 23),
        };
        if (eventName === "focus" || eventName === "focusin") {
          next.nodeIdBeforeKeyboard = id;
        }
        if (keyboardLikely && next.nodeIdAfterKeyboard === "—") {
          next.nodeIdAfterKeyboard = id;
          if (s.nodeIdBeforeKeyboard !== "—" && s.nodeIdBeforeKeyboard !== id) {
            next.remountSuspected = true;
          }
        }
        if ((eventName === "input" || eventName === "beforeinput") && !firstKeySeen.current) {
          firstKeySeen.current = true;
          next.nodeIdAfterFirstKey = id;
          if (s.nodeIdBeforeKeyboard !== "—" && s.nodeIdBeforeKeyboard !== id) {
            next.remountSuspected = true;
          }
        }
        if (eventName === "meta") {
          next.nodeIdAfterViewport = id;
          if (s.nodeIdBeforeKeyboard !== "—" && s.nodeIdBeforeKeyboard !== id) {
            next.remountSuspected = true;
          }
        }
        if (
          (eventName === "blur" || eventName === "meta") &&
          s.focused &&
          document.activeElement !== el &&
          keyboardLikely
        ) {
          next.focusStealSuspected = true;
        }
        return next;
      });
    },
    [testId],
  );

  const bind = useCallback(
    (el: HTMLElement | null) => {
      if (!el) return () => undefined;
      stampNode(el, testId);
      const names: Array<keyof Counters> = [
        "pointerdown",
        "click",
        "focus",
        "focusin",
        "beforeinput",
        "input",
        "change",
        "compositionstart",
        "compositionupdate",
        "compositionend",
        "keydown",
        "keyup",
        "blur",
      ];
      const handlers = names.map((name) => {
        const fn = () => bump(name, el);
        el.addEventListener(name, fn);
        return { name, fn };
      });
      const onVv = () => bump("meta", el);
      window.visualViewport?.addEventListener("resize", onVv);
      window.addEventListener("resize", onVv);
      return () => {
        for (const { name, fn } of handlers) el.removeEventListener(name, fn);
        window.visualViewport?.removeEventListener("resize", onVv);
        window.removeEventListener("resize", onVv);
      };
    },
    [bump, testId],
  );

  return { state, bind, bump };
}

/** TEST A — pure native uncontrolled DOM input (not React-controlled). */
function TestANative() {
  const hostRef = useRef<HTMLDivElement>(null);
  const { state, bind } = useImePanel("A");
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const input = document.createElement("input");
    input.type = "text";
    input.setAttribute("data-ime-test", "A");
    input.setAttribute("data-testid", "sv2-ime-test-a");
    input.autocomplete = "off";
    input.placeholder = "A: raw native uncontrolled — type норі";
    host.appendChild(input);
    const unbind = bind(input);
    return () => {
      unbind();
      host.removeChild(input);
    };
  }, [bind]);
  return (
    <section className="sv2-express-card" data-testid="sv2-ime-block-a">
      <h3>A — PURE NATIVE UNCONTROLLED</h3>
      <div ref={hostRef} />
      <TelemetryBox title="A" state={state} />
    </section>
  );
}

/** TEST B — isolated React local draft leaf. */
const TestBIsolatedReact = memo(function TestBIsolatedReact() {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { state, bind } = useImePanel("B");
  useEffect(() => bind(inputRef.current), [bind]);
  return (
    <section className="sv2-express-card" data-testid="sv2-ime-block-b">
      <h3>B — ISOLATED REACT LOCAL</h3>
      <div className="sv2-field">
        <label htmlFor="sv2-ime-b">B local draft</label>
        <input
          ref={inputRef}
          id="sv2-ime-b"
          data-ime-test="B"
          data-testid="sv2-ime-test-b"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="B: isolated React — type норі"
        />
      </div>
      <TelemetryBox title="B" state={state} />
    </section>
  );
});

/** TEST C — current shared IsolatedFormField (checkout abstraction) alone. */
function TestCSharedField() {
  const [value, setValue] = useState("");
  const { state, bind } = useImePanel("C");
  useEffect(() => {
    const el = document.getElementById("sv2-ime-c") as HTMLInputElement | null;
    return bind(el);
  }, [bind, value]);
  return (
    <section className="sv2-express-card" data-testid="sv2-ime-block-c">
      <h3>C — SHARED IsolatedFormField</h3>
      <IsolatedFormField
        id="sv2-ime-c"
        label="C shared field"
        value={value}
        onCommit={setValue}
        commitFilter={filterPersonNameInput}
        autoComplete="off"
        testId="sv2-ime-test-c"
        imeTestId="C"
      />
      <TelemetryBox title="C" state={state} />
    </section>
  );
}

/** TEST D — current page/search IsolatedSearchBar. */
function TestDPageSearch({ lang }: { lang: StoreV2Lang }) {
  const { state, bind } = useImePanel("D");
  useEffect(() => {
    const root = document.querySelector('[data-testid="sv2-ime-block-d"]');
    const el = root?.querySelector("input") as HTMLInputElement | null;
    return bind(el);
  }, [bind]);
  return (
    <section className="sv2-express-card" data-testid="sv2-ime-block-d">
      <h3>D — PAGE SEARCH (IsolatedSearchBar)</h3>
      <IsolatedSearchBar lang={lang} onSubmit={() => undefined} />
      <TelemetryBox title="D" state={state} />
    </section>
  );
}

/** TEST E — checkout-like parent state + IsolatedFormField (first name path). */
function TestECheckoutField() {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const parentRender = useRef(0);
  parentRender.current += 1;
  const { state, bind } = useImePanel("E");
  useEffect(() => {
    const el = document.getElementById("sv2-ime-e-name") as HTMLInputElement | null;
    return bind(el);
  }, [bind, name]);
  return (
    <section className="sv2-express-card" data-testid="sv2-ime-block-e">
      <h3>E — CHECKOUT FIELD INTEGRATION</h3>
      <p className="sv2-muted" style={{ marginTop: 0 }}>
        parentRenderCount={parentRender.current} (increments when parent re-renders)
      </p>
      <IsolatedFormField
        id="sv2-ime-e-name"
        label="E first name"
        value={name}
        onCommit={setName}
        commitFilter={filterPersonNameInput}
        autoComplete="given-name"
        testId="sv2-ime-test-e-name"
        imeTestId="E"
      />
      <IsolatedFormField
        id="sv2-ime-e-surname"
        label="E last name"
        value={surname}
        onCommit={setSurname}
        commitFilter={filterPersonNameInput}
        autoComplete="family-name"
        testId="sv2-ime-test-e-surname"
        imeTestId="E2"
      />
      <TelemetryBox title="E" state={state} />
    </section>
  );
}

export function ImeForensicDebugV2({ lang, onBack }: { lang: StoreV2Lang; onBack: () => void }): ReactNode {
  if (!isOwnerImeDebugAllowed()) {
    return (
      <div className="sv2-body" data-testid="sv2-ime-debug-denied">
        <p>IME debug denied.</p>
        <button type="button" className="sv2-cta" onClick={onBack}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="sv2-body" data-testid="sv2-ime-debug">
      <div className="sv2-section-title">168D2 IME forensic</div>
      <p className="sv2-muted">
        Owner-only. Type <strong>норі</strong> into A→E. Counters show lengths/events only — not text.
        Report which tests show characters.
      </p>
      <ViewportPanel />
      <TestANative />
      <TestBIsolatedReact />
      <TestCSharedField />
      <TestDPageSearch lang={lang} />
      <TestECheckoutField />
      <button type="button" className="sv2-cta" style={{ marginTop: 12 }} onClick={onBack} data-testid="sv2-ime-debug-back">
        {lang === "en" ? "Back" : "Назад"}
      </button>
    </div>
  );
}
